/**
 * Session token storage + reactive signals.
 *
 * The token lives in localStorage (service-worker-friendly — cookies on a
 * separate origin to the worker API would require SameSite=None). Listeners
 * receive updates so routed components can redirect when auth state changes.
 */

const TOKEN_KEY = 'concierge.operator.session'
const OPERATOR_KEY = 'concierge.operator.profile'

type Listener = (token: string | null) => void
const listeners = new Set<Listener>()

import type { SiteId } from '../types'

export interface StoredOperator {
  id: string
  email: string
  name: string | null
  sites: SiteId[]
  /** True for the public read-only demo login. */
  isDemo?: boolean
}

export function isDemoViewer(): boolean {
  return getStoredOperator()?.isDemo === true
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setSession(token: string, operator: StoredOperator): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(OPERATOR_KEY, JSON.stringify(operator))
  for (const l of listeners) l(token)
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(OPERATOR_KEY)
  for (const l of listeners) l(null)
}

export function getStoredOperator(): StoredOperator | null {
  const raw = localStorage.getItem(OPERATOR_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredOperator
  } catch {
    return null
  }
}

export function onSessionChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
